import type { FC, ReactNode } from 'react'

import { SectionHeading } from '@/components/landing/section-heading'
import { CRAFT_STATS, CRAFT_STEPS } from '@/constants/landing'

export const SavoirFaireSection: FC = (): ReactNode => {
    return (
        <section id='savoir-faire' aria-labelledby='savoir-faire-title' className='relative isolate overflow-hidden bg-espresso py-24 text-ivory sm:py-32'>
            <div aria-hidden='true' className='absolute inset-0 -z-10'>
                <div className='ambient-orb -left-32 top-10 size-[34rem] bg-gold/35' />
                <div className='ambient-orb right-[-10rem] top-1/3 size-[30rem] bg-champagne/20 [animation-delay:-6s]' />
                <div className='ambient-orb bottom-[-12rem] left-1/3 size-[36rem] bg-gold-deep/40 [animation-delay:-13s]' />
                <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_top,transparent_0%,rgba(20,17,14,0.55)_70%)]' />
            </div>

            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid gap-10 lg:grid-cols-[1fr_0.85fr] lg:items-end lg:gap-16'>
                    <SectionHeading
                        id='savoir-faire-title'
                        tone='dark'
                        eyebrow="Savoir-faire"
                        title={<>One pair of hands, <em className='text-gilded-light'>from first cut to final stitch</em></>}
                    />

                    <p className='reveal max-w-xl text-lg leading-relaxed text-ivory/65'>
                        An Aveline artisan follows a piece from the uncut hide to the last polish. Nothing is passed down a line, and nothing leaves the bench until the person who began it is satisfied.
                    </p>
                </div>

                <ul className='mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                    {CRAFT_STATS.map((stat) => (
                        <li key={stat.unit} className='glass glass-dark reveal flex flex-col rounded-[2rem] p-7 sm:p-8'>
                            <p className='text-gilded-light font-heading text-7xl font-light leading-none'>
                                {stat.value}
                            </p>

                            <p className='mt-4 text-[0.68rem] uppercase tracking-[0.26em] text-champagne/85'>
                                {stat.unit}
                            </p>

                            <p className='mt-8 text-sm leading-relaxed text-ivory/65'>
                                {stat.text}
                            </p>
                        </li>
                    ))}
                </ul>

                <ol className='mt-20 grid gap-10 border-t border-ivory/10 pt-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8'>
                    {CRAFT_STEPS.map((step) => (
                        <li key={step.step} className='reveal'>
                            <div className='flex items-center gap-4'>
                                <span className='flex size-12 items-center justify-center rounded-full bg-ivory/5 font-heading text-lg italic text-champagne ring-1 ring-ivory/15'>
                                    {step.step}
                                </span>

                                <span aria-hidden='true' className='h-px flex-1 bg-linear-to-r from-ivory/20 to-transparent' />
                            </div>

                            <h3 className='mt-6 font-heading text-3xl font-light'>
                                {step.title}
                            </h3>

                            <p className='mt-3 text-sm leading-relaxed text-ivory/60'>
                                {step.text}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}
